"use client"

import { useState, useEffect, useRef } from "react"
import "./SideMenu.css"
import { MainTracker } from '../../../PixelTrackers/MainTracker';
import { MainMenu } from './components/MainMenu';
import { CategoryDetailView } from './components/CategoryDetailView';
import { ContactUsView } from './components/ContactUsView';
import { clientConfig } from '../../../config';

interface SideMenuProps {
  isOpen: boolean
  onClose: () => void
  headerData: HeaderData | null
  language: string
  country: string
  menuOpenFrom?: string
}

export function SideMenu({ headerData, isOpen, onClose, language, country, menuOpenFrom = 'Header button' }: SideMenuProps) {
  const [currentView, setCurrentView] = useState<"main" | "category-detail" | "contact-us">("main")
  const [selectedCategory, setSelectedCategory] = useState<SidebarTopCategory | null>(null)
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const scrollableRef = useRef<HTMLDivElement>(null)
  
  // Global RP check for "Why Choose Us" section - same logic as CMS getIsReturningCustomer()
  // Only hide "Why Choose Us" if we have a valid last_paid_order (non-empty string that isn't 'undefined')
  const getIsGloballyReturningPatient = (): boolean => {
    try {
      const lastPaidOrder = (window as any).eventsTracking?.getData?.('last_paid_order');
      // Must be a non-empty string and not the literal 'undefined'
      return typeof lastPaidOrder === 'string' && lastPaidOrder.length > 0 && lastPaidOrder !== 'undefined';
    } catch {
      return false;
    }
  };
  const isGloballyReturningPatient = getIsGloballyReturningPatient();

  const trackEvent = (eventName: string, eventData: any) => {
    MainTracker.track('custom', eventName, eventData);
  };

  const handleScroll = () => {
    if (scrollableRef.current) {
      const scrollTop = scrollableRef.current.scrollTop
      setIsHeaderScrolled(scrollTop > 5)
    }
  };

  const resetMenuState = () => {
    setCurrentView("main")
    setSelectedCategory(null)
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = 0
    }
  };

  const trackMenuOpenEvent = () => {
    const device = window.innerWidth < 640 ? 'mobile' : 'desktop';
    const eventsTracking = (window as any).eventsTracking;
    const token = eventsTracking?.getData?.('token');
    const lastOrder = eventsTracking?.getData?.('last_order');
    const user_type = (token && lastOrder) ? 'RP' : 'FTB';

    const menu_open_from = menuOpenFrom;
    let open_from: string;
    const path = window.location.pathname.toLowerCase();
    if (path.includes('/account')) {
      open_from = 'account';
    } else if (path.includes('/contact')) {
      open_from = 'contact';
    } else if (path.includes('/deactivate-account')) {
      open_from = 'deactivate_account';
    } else if (path.includes('/change-email')) {
      open_from = 'change_email';
    } else {
      open_from = 'other';
    }

    trackEvent('menu_open', {
      source: 'navbar_click',
      device: device,
      default_tab: 'rx',
      menu_test: 'test',
      menu_open_from,
      open_from,
      user_type,
    });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen && !isClosing) {
      trackEvent('menu_close', { reason: 'esc' });
      setIsClosing(true)
      setTimeout(() => onClose(), 450)
    }
  };

  // Menu state management
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('side-menu-open')
      setIsClosing(false)
      resetMenuState()
      trackMenuOpenEvent()
      
      const scrollElement = scrollableRef.current
      if (scrollElement) {
        scrollElement.addEventListener('scroll', handleScroll)
        return () => scrollElement.removeEventListener('scroll', handleScroll)
      }
    } else {
      document.body.classList.remove('side-menu-open')
    }
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isClosing]);

  const handleCategoryClick = (category: SidebarTopCategory) => {
    setSelectedCategory(category)
    setCurrentView("category-detail")
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = 0
    }
  };

  const handleBackToMain = () => {
    setCurrentView("main")
    setSelectedCategory(null)
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = 0
    }
  };

  const handleChatWithUsClick = () => {
    setCurrentView("contact-us")
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = 0
    }
  };

  const handleClose = () => {
    if (!isClosing) {
      trackEvent('menu_close', { reason: 'x_button' });
      setIsClosing(true)
      setCurrentView("main");
      setSelectedCategory(null);
      setTimeout(() => onClose(), 450)
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!isClosing) {
      trackEvent('menu_close', { reason: 'outside_click' });
      setIsClosing(true)
      setTimeout(() => onClose(), 450)
    }
  };

  if (!headerData) return null;

  const rpCategoriesSet = new Set(headerData.rpCategories || []);
  const isRpForCategory = (mention: string | undefined): boolean => {
    if (!mention) return false;
    return rpCategoriesSet.has(mention);
  };

  const otcTopProducts = (headerData.promotedProductItems || []).map(item => ({
    id: String(item.id),
    name: item.name,
    price: item.price ? `€${parseFloat(String(item.price)).toFixed(2).replace('.', ',')}` : '€0,00',
    originalPrice: item.price_before_discount ? `€${parseFloat(String(item.price_before_discount)).toFixed(2).replace('.', ',')}` : null,
    discount: item.discount ? Math.floor(parseFloat(String(item.discount))) : null,
    image: `${clientConfig.IMAGES_URL}${item.imageLink.startsWith('/') ? item.imageLink.slice(1) : item.imageLink}`,
    url: item.url,
  }));

  return (
    <>
      {/* Backdrop */}
      <div 
        id="sideMenuBackdrop"
        className={`side-menu-backdrop ${!isOpen ? 'side-menu-closed' : isClosing ? 'closing' : 'side-menu-open'}`}
        onClick={handleBackdropClick}
      />

      {/* Side Menu */}
      <div 
        id="sideMenuContainer" 
        className={`side-menu-container ${!isOpen ? 'side-menu-closed' : isClosing ? 'side-menu-closing' : 'side-menu-open'}`}
      >
        <div id="sideMenuInner" className="side-menu-inner" ref={scrollableRef}>
          <div className={`side-menu-header-sticky ${isHeaderScrolled ? "scrolled" : ""}`}>
            <div className="bg-[#f6f7f9] rounded-tl-[30px] md:rounded-tl-none px-7 md:px-8 py-4 flex items-center justify-between">
              {(currentView === "category-detail" || currentView === "contact-us") ? (
                <button onClick={handleBackToMain} className="h-8 w-8 text-text-primary flex items-center justify-center bg-transparent border-none cursor-pointer text-[2rem] transition-colors duration-200 ease-in-out hover:text-text-secondary" id="backButton" aria-label="Back">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              ) : (
                <span className="text-[1.5rem] md:text-[1.25rem] font-normal text-text-primary font-header leading-[1.5]" id="menuTitle">Menu</span>
              )}
              <button onClick={handleClose} className="h-6 w-6 text-[#98a0af] flex items-center justify-center bg-transparent border-none cursor-pointer transition-colors duration-200 ease-in-out hover:text-text-secondary" aria-label="Close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          <div id="sideMenuContent" className="side-menu-content bg-white">
            {currentView === "contact-us" ? (
              <ContactUsView language={language} country={country} />
            ) : currentView === "category-detail" && selectedCategory ? (
              <CategoryDetailView
                category={selectedCategory}
                onBack={handleBackToMain}
                language={language}
                country={country}
                isReturningPatient={isRpForCategory(selectedCategory.mention)}
                isGloballyReturningPatient={isGloballyReturningPatient}
                otcTopProducts={otcTopProducts}
              />
            ) : (
              <MainMenu
                headerData={headerData}
                onCategoryClick={handleCategoryClick}
                onChatWithUsClick={handleChatWithUsClick}
                language={language}
                country={country}
                isReturningPatient={isGloballyReturningPatient}
              />
            )}

          </div>
        </div>
      </div>
    </>
  )
}
